import React, { useRef } from "react";
import {
    createSearchParams,
    useNavigate,
} from "react-router-dom";
import { getTranslation } from "common";
import { Stack, TextField, Button, Typography, Box } from "@mui/material";
import { useTranslations, useForm } from "hooks";
import { isEmail, isRequired, Validation } from "hooks/useForm";
import { noTokenApi } from "api";
import OAuth2 from "components/OAuth2";
import { useContext } from "react";
import { AuthContext } from "contexts";
import { usersApi } from "api";
import { noSpecialChars } from "common/utilities";
import LandingImage from "image/LandingImage.png";
import Footer from "components/Footer";
import Navbar from "components/Navbar";
import LandingCore from "components/LandingCore";

export default function LandingPage() {
    const translations = useTranslations();
    const emailInputRef = useRef<HTMLInputElement>(null);
    // const dispatch = useDispatch();
    const {
        actions: { setRegisterUser, setUser },
    } = useContext(AuthContext);
    const navigate = useNavigate();
    const enterWithGoogle = async (token: string, registrated: boolean) => {
        try {
            let result = await usersApi.get(`${token}`);
            if (registrated) {
                //se già registrato, vai alla dashboard
                setUser({
                    jwt: token,
                    userId: result.data.user.id,
                    email: result.data.user.email,
                    username: result.data.user.username,
                    firstname: result.data.user.firstname,
                    lastname: result.data.user.lastname,
                    picture: result.data.user.picture,
                    linkprofile: result.data.user.linkprofile,
                    subscriptiontype: result.data.user.subscriptiontype,
                    i18n: result.data.user.i18n,
                    dob: result.data.user.dob,
                });
                navigate("/dashboard/organizations");
            } else {
                //se nuovo, imposta i dati per la registrazione
                setRegisterUser({
                    userId: result.data.user.id,
                    jwt: token,
                    ...result.data.user,
                });
                navigate("/organization");
            }
        } catch (error) {
            console.error("Error during Google login:", error);
        }
    };

    const validations: Validation[] = [
        ({ email }) =>
            isRequired(email) || {
                email: getTranslation(translations, "register.valid.Error_after_at"),
            },
        ({ email }) =>
            isEmail(email) || {
                email: getTranslation(translations, "register.valid.Error_after_at"),
            },
        ({ email }) =>
            noSpecialChars(email) || {
                email: getTranslation(translations, "register.valid.Error_invalid_chars"),
            },
    ];
    const { values, errors, touched, changeHandler, isValid } = useForm(
        { email: "" },
        validations
    );

    const submitEmail = async (e: React.FormEvent<HTMLButtonElement>) => {
        e.preventDefault();
        const response = await noTokenApi.get(
            `users/emailinplatform/${values.email}`
        );
        if (response.status === 200) {
            // dispatch(addEmail(values.email));
            //navigate(response.data.mailexist ? "/login" : "/registration", { state: { email: values.email } });
            navigate({
                pathname: response.data.mailexist ? "/login" : "/registration",
                search: createSearchParams({ email: values.email }).toString(),
            });
        }
    };

    return (
        <Box>
            <Navbar />
            <Stack
                id="email-form-section"
                direction={{ xs: "column", md: "row" }}
                justifyContent="center"
                alignItems="center"
                sx={{ mt: { xs: 5 }, mx: "auto", maxWidth: 1200, px: 6 }}
            >
                {/* Left Column - Content & Form */}
                <Stack
                    sx={{
                        width: "100%",
                        textAlign: { xs: "center", md: "left" },
                        alignItems: { xs: "center", md: "flex-start" },
                    }}
                >
                    <Stack spacing={4}>
                        <Typography variant="h3" component="h1" sx={{ fontWeight: "bold", marginBottom: "10px" }}>
                            {getTranslation(translations, "flowenti_landing.title")}
                        </Typography>
                        <Typography variant="h6" color="text.secondary">
                            {getTranslation(translations, "flowenti_landing.subtitle")}
                        </Typography>
                    </Stack>
                    <Stack
                        component="form"
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        width="100%"
                        sx={{ marginTop: "20px", alignItems: "start" }}
                    >
                        <TextField
                            inputRef={emailInputRef}
                            // fullWidth
                            size="small"
                            id="welcome-email"
                            label={getTranslation(translations, "user.email.label")}
                            value={values.email}
                            onChange={({ target }) => changeHandler("email", target.value)}
                            error={Boolean(touched.email && errors.email)}
                            helperText={(touched.email && errors.email) || ' '}
                            FormHelperTextProps={{
                                sx: { fontSize: 11, width: "100%", ml: 0 },
                            }}
                            type="email"
                            aria-controls="email"
                            sx={{ height: 37, minWidth: 223 }}
                        />
                        <Button
                            size="small"
                            // fullWidth
                            id="welcome-button"
                            type="submit"
                            variant="contained"
                            onClick={submitEmail}
                            disabled={!isValid}
                            sx={{
                                textTransform: "none",
                                fontSize: "0.9rem",
                                height: 37,
                            }}
                        >
                            {getTranslation(translations, "register.label.button_register_is_free")}
                        </Button>
                        {/* {void console.log("process.env.REACT_APP_ENV: ", process.env.REACT_APP_ENV)} */}
                        {process.env.REACT_APP_ENV === "development" &&
                            <OAuth2
                                login={false}
                                onGoogleConfirm={enterWithGoogle}
                                showLoginButton={false}
                            />
                        }
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 5 }}>{getTranslation(translations, "landing_no.credit.card")}</Typography>
                </Stack>

                {/* Right Column - Image */}
                <Box
                    component="img"
                    sx={{
                        height: { xs: 'auto', md: 'auto' },
                        width: "auto",
                        maxWidth: '100%',
                        maxHeight: "30dvh",
                        marginBottom: "20px"
                    }}
                    src={LandingImage}
                    alt="Flowenti Platform Illustration"
                />
            </Stack>
            <LandingCore focusEmailField={() => emailInputRef.current?.focus()} />
            {/* Sezione Recensioni */}
            {/* <Box id="recensioni" sx={{ py: 6, px: 4 }}>
                <Container maxWidth="md">
                    <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 4 }}>
                        {getTranslation(translations, "users_reviews.title")}
                    </Typography>
                    <Stack spacing={4}>
                        <Box
                            sx={{
                                p: 3,
                                borderRadius: 2,
                                backgroundColor: "white",
                                boxShadow: 2,
                            }}
                        >
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                “{getTranslation(translations, "user1_review")}”
                            </Typography>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                Martina R. – Project Manager
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                p: 3,
                                borderRadius: 2,
                                backgroundColor: "white",
                                boxShadow: 2,
                            }}
                        >
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                “{getTranslation(translations, "user2_review")}”
                            </Typography>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                Luca D. – Team Leader
                            </Typography>
                        </Box>
                    </Stack>
                </Container>
            </Box> */}
            <Footer />
        </Box >
    );
}
